import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function Segmented({ value, options, onChange, testid, size = "md" }) {
  return (
    <div role="radiogroup" className="inline-flex flex-wrap rounded-lg border border-[#E2E8F0] bg-[#F7FAF8] p-0.5 dark:border-[#223139] dark:bg-white/5" data-testid={testid}>
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const on = value === val;
        return (
          <button key={val} type="button" role="radio" aria-checked={on} onClick={() => onChange(on && size === "toggle-off" ? "" : val)}
            data-testid={`${testid}-${String(val).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={cn("rounded-md font-semibold transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A85A]",
              size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-3.5 py-1.5 text-xs",
              on ? "bg-white text-[#008F4D] shadow-sm dark:bg-[#00A85A]/20 dark:text-[#3FD98A]" : "text-[#687173] hover:text-[#101516] dark:text-[#8B9698] dark:hover:text-white")}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function Chips({ options, selected = [], onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <button key={o} type="button" aria-pressed={on} onClick={() => onToggle(o)} data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A85A]",
              on ? "border-[#00A85A] bg-[#00A85A] text-white" : "border-[#E2E8F0] bg-white text-[#4E5F68] hover:border-[#00A85A]/50 dark:border-[#223139] dark:bg-white/5 dark:text-[#8B9698]")}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export const toggleIn = (arr = [], v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export function MmInput({ label, value, onChange, testid, max = 80, unit = "mm", decimals = 1 }) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  const re = decimals ? new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`) : /^\d+$/;
  const ok = (v) => v === "" || (re.test(v) && Number(v) >= 0 && Number(v) <= max);
  const bad = !ok(String(draft));
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{label}</span>
      <div className="relative mt-1">
        <input type="text" inputMode="decimal" value={draft} onChange={(e) => { const v = e.target.value.trim(); setDraft(v); if (ok(v)) onChange(v); }} data-testid={testid} aria-invalid={bad}
          className={cn("h-9 w-full rounded-lg border bg-white pl-2.5 pr-9 text-sm text-[#101516] focus:outline-none dark:bg-white/5 dark:text-white", bad ? "border-red-400" : "border-[#E2E8F0] focus:border-[#00A85A] dark:border-[#223139]")} />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-[#8B9698]">{unit}</span>
      </div>
      {bad && <span className="mt-0.5 block text-[10px] text-red-500" data-testid={`${testid}-error`}>0–{max}{decimals ? ", 1 decimal" : ""} · not saved</span>}
    </label>
  );
}

export function Field({ label, children }) {
  return <div><div className="mb-1.5 text-xs font-semibold text-[#4E5F68] dark:text-[#8B9698]">{label}</div>{children}</div>;
}

export function TextIn({ value, onChange, testid, placeholder, className }) {
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className={cn("h-9 w-full rounded-lg border border-[#E2E8F0] bg-white px-2.5 text-sm dark:border-[#223139] dark:bg-white/5 dark:text-white", className)} />;
}

export function Pick({ value, onChange, options, testid, placeholder = "Select…" }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full rounded-lg border border-[#E2E8F0] bg-white px-2.5 text-sm dark:border-[#223139] dark:bg-white/5 dark:text-white">
      <option value="">{placeholder}</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export function SectionCard({ title, subtitle, children, right, testid }) {
  return (
    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 dark:border-[#223139] dark:bg-[#0E1A21]" data-testid={testid}>
      {(title || right) && (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div><h3 className="text-sm font-bold text-[#101516] dark:text-white">{title}</h3>{subtitle && <p className="mt-0.5 text-xs text-[#8B9698]">{subtitle}</p>}</div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function ConfirmDialog({ open, title, description, confirmLabel = "Confirm", onConfirm, onCancel, destructive }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{description}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-ok" onClick={onConfirm} className={destructive ? "bg-red-600 hover:bg-red-700" : "bg-[#00A85A] hover:bg-[#008F4D]"}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
